import { memo } from 'react'
import styled from '@emotion/styled'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import Box from './Box'
import GrooverText from './GrooverText'
import { Enrollment, EnrollmentPaymentStatus, EnrollmentStatus } from '../../../pure-js/types/GrooverTypes'
import { DiscountUserInputV3 } from '../../../pure-js/types/DiscountTypes'

type Props = {
  enrollment: Enrollment
  onResume: () => void
  onStartNew: () => void
  isOpen: boolean
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: rgba(39, 42, 48, 1);
  margin: 0 0 12px;
`

const Description = styled.p`
  font-size: 14px;
  color: rgba(39, 42, 48, 0.7);
  margin: 0 0 16px;
  line-height: 1.5;
`

const CalloutBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  border-radius: 12px;
  padding: 12px 16px;
  text-align: left;
  cursor: default;
`

const StatusInfoBox = styled(CalloutBox)`
  background-color: #f3f8ff;
  border: 1px solid #cce1ff;
  margin-bottom: 16px;

  & span {
    color: rgba(39, 42, 48, 0.85) !important;
    font-size: 14px;
    line-height: 1.5;
  }
`

const UserInfoText = styled.p`
  font-size: 13px;
  color: rgba(39, 42, 48, 0.6);
  margin: 0 0 16px;
`

const ResumeButton = styled.button`
  width: 100%;
  border: none;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 12px;
  background-color: rgba(59, 89, 236, 1);
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 10px;

  & span {
    color: #ffffff !important;
  }

  &:hover {
    background-color: rgba(40, 65, 190, 1);
  }
`

const StartNewButton = styled.button`
  width: 100%;
  border: 1px solid rgba(39, 42, 48, 0.2);
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 12px;
  background-color: transparent;
  color: rgba(39, 42, 48, 0.8);
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;

  &:hover {
    background-color: rgba(39, 42, 48, 0.05);
  }
`

const WarningBox = styled(CalloutBox)`
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  padding: 10px 14px;
  margin-top: 4px;

  & span {
    color: #9a3412 !important;
    font-size: 13px;
    line-height: 1.4;
  }
`

const getStatusTextKey = (status: EnrollmentStatus, paymentStatus: EnrollmentPaymentStatus): string => {
  if (paymentStatus === EnrollmentPaymentStatus.PartlyPaid) {
    return 'resumeRegistrationDialogStatusPartlyPaid'
  }
  if (status === EnrollmentStatus.Pending) {
    return 'resumeRegistrationDialogStatusPendingUnpaid'
  }
  if (status === EnrollmentStatus.PartlyApproved) {
    return 'resumeRegistrationDialogStatusPartlyApproved'
  }
  return 'resumeRegistrationDialogStatusConfirmedUnpaid'
}

const getPartnerName = (enrollment: Enrollment): string | undefined => {
  // V3: partnerUser is present with obfuscated name
  if (enrollment.partnerUser?.name) {
    return enrollment.partnerUser.name
  }
  // V4: partnerUser absent, but registeredPartners may contain a name
  const v3Input = enrollment.discountUserInput as DiscountUserInputV3
  const firstPartner = v3Input?.registeredPartners?.[0]
  return firstPartner?.linkedUserName ?? firstPartner?.enteredName
}

const ResumeRegistrationDialog = ({ enrollment, onResume, onStartNew, isOpen }: Props) => {
  if (!isOpen) return null

  const partnerName = getPartnerName(enrollment)
  const statusTextKey = getStatusTextKey(enrollment.status, enrollment.paymentStatus)

  return (
    <Overlay onClick={onStartNew}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>
          <GrooverText textKey="resumeRegistrationDialogTitle" />
        </Title>
        <Description>
          <GrooverText textKey="resumeRegistrationDialogDescription" />
        </Description>
        {enrollment.mainUser?.name && (
          <UserInfoText>
            {enrollment.mainUser.name}
            {partnerName ? ` · ${partnerName}` : ''}
          </UserInfoText>
        )}
        <StatusInfoBox role="status">
          <InfoOutlined sx={{ width: 20, height: 20, color: '#3b59ec', flexShrink: 0, marginTop: '2px' }} aria-hidden />
          <GrooverText textKey={statusTextKey} />
        </StatusInfoBox>
        <Box fullWidth gap="0px">
          <ResumeButton type="button" onClick={onResume}>
            <GrooverText textKey="resumeRegistrationDialogResumeButton" />
          </ResumeButton>
          <StartNewButton type="button" onClick={onStartNew}>
            <GrooverText textKey="resumeRegistrationDialogNewButton" />
          </StartNewButton>
          <WarningBox role="note">
            <WarningAmberOutlined
              sx={{ width: 20, height: 20, color: '#9a3412', flexShrink: 0, marginTop: '1px' }}
              aria-hidden
            />
            <GrooverText textKey="resumeRegistrationDialogNewButtonWarning" />
          </WarningBox>
        </Box>
      </Card>
    </Overlay>
  )
}

export default memo(ResumeRegistrationDialog)
